import {
  Grid,
  ONE_COLUMN_MAX_WIDTH,
  TWO_COLUMN_MAX_WIDTH,
} from '@spotify-internal/frodor-modules';
import React, { useState } from 'react';
import { useVideosData } from './useVideosData';
import {
  Text,
  ButtonSecondary,
  cssColorValue,
  cssSpacing,
} from '@spotify-internal/encore-web';
import styled from 'styled-components';
import { VideoTile } from './VideoTile';
import { useRouter } from 'next/router';
import { useT } from '../../../features/src/i18n';
import { VideoType } from './types';
import { InteractiveElement } from '../../utils/InteractiveElement';

const VIDEOS_PER_ROW = 3;

const StyledGrid = styled(Grid)`
  row-gap: var(--encore-spacing-looser-3);
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: 1 / -1;
  margin-bottom: calc(-1 * (var(--encore-spacing-looser-3) - 24px));
`;

const VideoTileWrapper = styled(VideoTile)`
  grid-column: span 4;
  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    grid-column: span 1;
  }
  @media (max-width: ${ONE_COLUMN_MAX_WIDTH}) {
    grid-column: 1 / -1;
  }
`;
const ShowMoreWrapper = styled.div`
  grid-column: 1 / -1;
  padding-top: ${cssSpacing('base')};
  position: relative;
  text-align: center;

  &::before {
    border-bottom: 1px solid ${cssColorValue('textSubdued')};
    content: '';
    display: block;
    position: absolute;
    width: 100%;
  }
`;
export const ButtonWrapper = styled.span`
  background-color: ${cssColorValue('backgroundBase')};
  display: inline-block;
  padding-inline: ${cssSpacing('tighter-2')};
  position: relative;
  transform: translate(0, -24px);
`;
export const VideoGroup = ({
  groupId,
  showMoreButton,
  className,
}: {
  groupId: string;
  showMoreButton: boolean;
  className?: string;
}) => {
  const t = useT();
  const { videoGroups, videos } = useVideosData();
  const { locale } = useRouter();
  const [allShowing, setAllShowing] = useState(!showMoreButton);
  const group = videoGroups.byId[groupId];
  const groupVideos = group.videos.map(
    (videoId: string) => videos.byId[videoId],
  );
  const videosToDisplay =
    showMoreButton && !allShowing ? VIDEOS_PER_ROW : undefined;
  const translatedVideos =
    locale !== 'en'
      ? groupVideos.filter((i: $TSFixMe) => i.translated)
      : groupVideos;
  const uniqueTranslatedVideos = translatedVideos?.filter(
    (i: $TSFixMe, index: $TSFixMe) => translatedVideos.indexOf(i) === index,
  );

  if (uniqueTranslatedVideos.length < 1) {
    return null;
  }
  return (
    <StyledGrid className={className}>
      <TextWrapper>
        <Text variant="titleMedium">{group.name}</Text>
        {group.body && <Text>{group.body}</Text>}
      </TextWrapper>
      {uniqueTranslatedVideos
        .slice(0, videosToDisplay)
        .map((video: VideoType) => (
          <VideoTileWrapper key={video.id} video={video} />
        ))}
      {showMoreButton && uniqueTranslatedVideos.length > VIDEOS_PER_ROW && (
        <ShowMoreWrapper>
          <ButtonWrapper>
            <InteractiveElement
              customComponent={ButtonSecondary}
              size="medium"
              onClick={() => {
                setAllShowing(!allShowing);
              }}
              trackingLabel={`Group - ${group.name} - Show ${
                allShowing ? 'Less' : 'More'
              }`}
              trackingCategory="Videos"
            >
              {allShowing
                ? t(
                    'FRODOR_SHOW_LESS_VIDEOGROUP',
                    'Show Less',
                    'Show less information',
                  )
                : t(
                    'FRODOR_SHOW_MORE_VIDEOGROUP',
                    'Show More',
                    'Show more information',
                  )}
            </InteractiveElement>
          </ButtonWrapper>
        </ShowMoreWrapper>
      )}
    </StyledGrid>
  );
};
